import { ToBillArguments, ToBillEvent } from '../../models/Factura';
import { CfdiIndexEntry, CfdiIndexFilter, CFDI_TIPO_LABEL } from '../../models/CfdiIndex';

export interface EditRoute {
    pathname: string;
    state: {
        billArgument: ToBillArguments<ToBillEvent>;
        billEvents: ToBillEvent[];
        paymentEvents: ToBillEvent[];
    };
}

/**
 * Port of the legacy MisFacturas.onConfirmToEdit: given the full argument (already stripped of __typename) and the
 * uuid to edit, resolve the facturación route that owns that CFDI. Returns null when the uuid is not part
 * of the argument (e.g. an already substituted CFDI).
 */
export const resolveEditRoute = (argument: ToBillArguments<ToBillEvent>, uuid: string): EditRoute | null => {
    if (!argument) return null;
    const billEvents = argument.billEvents || [];
    const paymentEvents = argument.paymentEvents || [];
    const baseState = { billArgument: argument, billEvents, paymentEvents };

    if (argument.uuid && argument.uuid === uuid) {
        return { pathname: `/facturacion/facturar/${argument._id}`, state: baseState };
    }
    const billEvent = billEvents.find(bE => bE.uuid === uuid);
    if (billEvent) {
        // Base CFDI of a billEvent (type "I"): edited standalone, same as the legacy view
        const editBaseArgument: ToBillArguments<ToBillEvent> = {
            multipayType: 0,
            timeDefered: 0,
            multiPayments: 1,
            upfront: 0,
            frequency: 'monthly',
            isClosed: false,
            createdAt: Date.now(),
            billEvents: [],
            paymentEvents: [],
            MasterCFDI: billEvent.CFDI,
        };
        return {
            pathname: `/facturacion/facturar/${billEvent.argumentId}/${billEvent._id}`,
            state: { billArgument: editBaseArgument, billEvents: [], paymentEvents: [] },
        };
    }
    const notaEvent = billEvents.find(bE => bE.notaUUID === uuid);
    if (notaEvent) {
        return { pathname: `/facturacion/notacredito/${notaEvent._id}`, state: baseState };
    }
    const payEvent = paymentEvents.find(pE => pE.uuid === uuid);
    if (payEvent) {
        return { pathname: `/facturacion/setup/pagos/${payEvent._id}`, state: baseState };
    }
    return null;
};

/** First line of the "Tipo" column */
export const entryTypeLabel = (entry: CfdiIndexEntry): string => {
    if (entry.specialType === 'cartaporte') return 'Carta porte';
    if (entry.tipoDeComprobante === 'I') {
        switch (entry.multipayType) {
            case 1: return 'Factura con adelanto';
            case 2: return 'Pago en parcialidades';
            case 3: return 'Parcialidades con adelanto';
            default: return 'Factura';
        }
    }
    return CFDI_TIPO_LABEL[entry.tipoDeComprobante] || entry.tipoDeComprobante;
};

/** Second line of the "Tipo" column: the role inside its relationship group */
export const entryRoleLabel = (entry: CfdiIndexEntry): string => {
    switch (entry.kind) {
        case 'master':
            if (entry.multipayType === 1) return 'Adelanto';
            if (entry.multipayType === 2 || entry.multipayType === 3) return 'Factura del total';
            return '';
        case 'billEvent':
            return entry.multipayType === 1 || entry.multipayType === 3 ? 'Liquidación' : 'Factura relacionada';
        case 'notaCredito':
            return 'Nota de crédito';
        case 'pago':
            return entry.paymentNum ? `Parcialidad ${entry.paymentNum}` : 'Complemento de pago';
        default:
            return 'Sin relación registrada';
    }
};

export const shortUuid = (uuid?: string): string => uuid ? `${uuid.substring(0, 8)}…${uuid.substring(uuid.length - 4)}` : '';

export const isUuidLike = (value: string): boolean => /^[0-9a-f]{8}(-[0-9a-f]{0,4}){0,3}(-[0-9a-f]{0,12})?$/i.test(value) || /^[0-9a-f-]{8,36}$/i.test(value) && /[0-9]/.test(value) && /[a-f]/i.test(value);

/**
 * Interprets the free-text search box into filter fields:
 *  - UUID (full or prefix)           -> uuid
 *  - "A-123", "A 123", "A/123", "A123" -> serie + folio
 *  - "123"                           -> folio
 *  - anything else                   -> nombre (prefix)
 */
export const parseSearchText = (raw: string): Pick<CfdiIndexFilter, 'uuid' | 'serie' | 'folio' | 'nombre'> => {
    const text = (raw || '').trim();
    if (!text) return {};
    if (isUuidLike(text)) return { uuid: text.toUpperCase() };
    const serieFolio = /^([A-Za-z]{1,10})[-\s/]?(\d{1,12})$/.exec(text);
    if (serieFolio) return { serie: serieFolio[1].toUpperCase(), folio: serieFolio[2] };
    if (/^\d{1,12}$/.test(text)) return { folio: text };
    return { nombre: text };
};

/** Inverse of parseSearchText, to hydrate the search box from a URL filter */
export const searchTextFromFilter = (filter: CfdiIndexFilter): string => {
    if (filter.uuid) return filter.uuid;
    if (filter.serie && filter.folio) return `${filter.serie}-${filter.folio}`;
    if (filter.folio) return filter.folio;
    if (filter.nombre) return filter.nombre;
    return '';
};

export type TipoFilterValue = 'ALL' | 'I' | 'E' | 'P' | 'T' | 'N' | 'ADELANTO' | 'PARCIALIDAD' | 'PARCIALIDAD_ADELANTO' | 'CARTA_PORTE';

export const TIPO_FILTER_OPTIONS: { value: TipoFilterValue; label: string; disabled?: boolean }[] = [
    { value: 'ALL', label: 'Todos los tipos' },
    { value: 'I', label: 'Facturas (Ingreso)' },
    { value: 'E', label: 'Notas de crédito (Egreso)' },
    { value: 'P', label: 'Complementos de pago' },
    { value: 'T', label: 'Traslados' },
    // [Sept 08, 2026] >>> Nomina is muted for now as it's not supported yet
    // { value: 'N', label: 'Nómina' },
    { value: 'ALL', label: '────────────', disabled: true },
    { value: 'ADELANTO', label: 'Facturas con adelanto' },
    { value: 'PARCIALIDAD', label: 'Pago en parcialidades' },
    { value: 'PARCIALIDAD_ADELANTO', label: 'Parcialidades con adelanto' },
    { value: 'CARTA_PORTE', label: 'Carta porte' },
];

export const applyTipoFilter = (filter: CfdiIndexFilter, value: TipoFilterValue): CfdiIndexFilter => {
    const next: CfdiIndexFilter = { ...filter };
    delete next.tipoDeComprobante;
    delete next.multipayType;
    delete next.specialType;
    switch (value) {
        case 'I': case 'E': case 'P': case 'T': case 'N':
            next.tipoDeComprobante = value; break;
        case 'ADELANTO': next.multipayType = 1; break;
        case 'PARCIALIDAD': next.multipayType = 2; break;
        case 'PARCIALIDAD_ADELANTO': next.multipayType = 3; break;
        case 'CARTA_PORTE': next.specialType = 'cartaporte'; break;
        default: break;
    }
    return next;
};

export const tipoFilterValueFrom = (filter: CfdiIndexFilter): TipoFilterValue => {
    if (filter.specialType === 'cartaporte') return 'CARTA_PORTE';
    if (filter.multipayType === 1) return 'ADELANTO';
    if (filter.multipayType === 2) return 'PARCIALIDAD';
    if (filter.multipayType === 3) return 'PARCIALIDAD_ADELANTO';
    if (filter.tipoDeComprobante) return filter.tipoDeComprobante;
    return 'ALL';
};

export const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
    { value: '', label: 'Todos los estados' },
    { value: 'vigente', label: 'Vigentes' },
    { value: 'cancelada', label: 'Canceladas' },
    { value: 'sustituida', label: 'Sustituidas' },
    { value: 'cancelando', label: 'En cancelación' },
];
